import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "./sidebar";
import { cn } from "../../lib/utils";
import { TbLogout2, TbArrowsExchange } from "react-icons/tb";
import { MdKeyboardArrowDown } from "react-icons/md";

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
  subItems?: {
    title: string;
    url: string;
  }[];
}

interface AppSidebarProps {
  items: NavItem[];
  onLogout: () => void;
  onChangePassword: () => void;
}

export function AppSidebar({
  items,
  onLogout,
  onChangePassword,
}: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [openSubMenus, setOpenSubMenus] = React.useState<string[]>([]);

  const toggleSubMenu = (title: string) => {
    setOpenSubMenus((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : [...prev, title]
    );
  };

  const isActive = (url: string) => {
    if (url === "/dashboard" || url === "/sales-dashboard") {
      return location.pathname === url;
    }
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-gray-100 transition-[width] duration-300 ease-in-out">
      <SidebarHeader
        className={cn(
          "h-20 flex flex-row items-center overflow-hidden",
          isCollapsed ? "justify-center px-2" : "px-4 gap-3"
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center flex-shrink-0">
          <img
            src="/assets/log.png"
            alt="logo"
            className={cn(isCollapsed ? "w-6 h-6" : "h-10 w-10")}
          />
        </div>
        <div className={cn("flex flex-col overflow-hidden transition-all duration-300", isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}>
          <span className="text-xl font-bold text-gray-900 tracking-tight truncate whitespace-nowrap">
            Foodbank
          </span>
          <span className="text-xs text-gray-400 font-medium -mt-1 whitespace-nowrap">
            AO'S Dashboard
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent className={cn("px-2 pt-4", isCollapsed ? "w-full flex items-center flex-col" : "w-auto")}>
        <SidebarMenu>
          {items.map((item) => {
            const active = isActive(item.url);
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isOpen = openSubMenus.includes(item.title);

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild={!hasSubItems}
                  isActive={active}
                  tooltip={item.title}
                  className={cn(
                    "h-10 rounded-xl transition-all duration-200 px-2",
                    isCollapsed ? "flex items-center justify-center" : "",
                    active && !hasSubItems
                      ? "bg-[#008751] text-white hover:bg-[#007a49] hover:text-white shadow-md shadow-green-100"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-900",
                  )}
                  onClick={hasSubItems ? () => toggleSubMenu(item.title) : undefined}
                >
                  {hasSubItems ? (
                    <div className={cn("flex items-center w-full", isCollapsed ? "justify-center" : "justify-between px-1")}>
                      <div className="flex items-center gap-3">
                        <item.icon
                          className={cn(
                            "h-5 w-5 flex-shrink-0",
                            active ? "text-[#008751]" : "text-gray-400",
                          )}
                        />
                        <span className={cn("font-bold text-sm truncate transition-all duration-300", isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}>
                          {item.title}
                        </span>
                      </div>
                      {!isCollapsed && (
                        <MdKeyboardArrowDown className={cn("h-4 w-4 transition-transform duration-200", isOpen ? "rotate-180" : "")} />
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate(item.url)}
                      className={cn(
                        " w-full flex items-center",
                        isCollapsed ? "justify-center gap-0" : "gap-3"
                      )}
                    >
                      <item.icon
                        className={cn(
                          "h-5 w-5 flex-shrink-0",
                          active ? "text-white" : "text-gray-400",
                        )}
                      />
                      <span className={cn("font-bold text-sm truncate transition-all duration-300", isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}>
                        {item.title}
                      </span>
                    </button>
                  )}
                </SidebarMenuButton>
                {hasSubItems && isOpen && !isCollapsed && (
                  <SidebarMenuSub>
                    {item.subItems?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === subItem.url}
                        >
                          <button
                            onClick={() => navigate(subItem.url)}
                            className={cn(
                              "w-full text-left px-2 py-1.5 rounded-lg transition-colors",
                              location.pathname === subItem.url
                                ? "text-[#008751] font-semibold bg-green-50"
                                : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                            )}
                          >
                            {subItem.title}
                          </button>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter className="p-3 border-t border-gray-50">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={false}
              tooltip="Change Password"
              className={cn(
                "h-11 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-900 px-3",
                isCollapsed && "justify-center gap-0"
              )}
              onClick={onChangePassword}
            >
              <TbArrowsExchange className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span className={cn("font-medium text-sm transition-all duration-300 truncate", isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}>Change Password</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={false}
              tooltip="Logout"
              className={cn(
                "h-11 rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 px-3 transition-colors",
                isCollapsed && "justify-center gap-0"
              )}
              onClick={onLogout}
            >
              <TbLogout2 className="h-5 w-5 flex-shrink-0" />
              <span className={cn("font-medium text-sm transition-all duration-300 truncate", isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100")}>Log Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
